/**
 * useResponsive Hook
 *
 * Provides responsive utilities and breakpoint detection across all platforms
 * Automatically adjusts layouts based on device type and screen size
 */

import { useState, useEffect } from 'react';
import { Dimensions, Platform, useWindowDimensions } from 'react-native';

// Breakpoint definitions (matching common standards)
export const BREAKPOINTS = {
  xs: 0, // Extra small (phones in portrait)
  sm: 576, // Small (phones in landscape)
  md: 768, // Medium (tablets in portrait)
  lg: 992, // Large (tablets in landscape, small laptops)
  xl: 1200, // Extra large (desktops)
  xxl: 1600, // Extra extra large (large desktops)
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;
export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type Orientation = 'portrait' | 'landscape';

export interface ResponsiveInfo {
  // Screen dimensions
  width: number;
  height: number;

  // Device type
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;

  // Orientation
  orientation: Orientation;
  isPortrait: boolean;
  isLandscape: boolean;

  // Breakpoints
  breakpoint: Breakpoint;
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  isXxl: boolean;

  // Utility functions
  greaterThan: (breakpoint: Breakpoint) => boolean;
  lessThan: (breakpoint: Breakpoint) => boolean;
  between: (min: Breakpoint, max: Breakpoint) => boolean;
}

/**
 * Detect device type based on screen width and platform
 */
function getDeviceType(width: number): DeviceType {
  // On web, use user agent as additional hint
  if (Platform.OS === 'web') {
    const ua = navigator.userAgent;
    if (/Mobile|Android|iPhone|iPod/.test(ua) && width < BREAKPOINTS.md) {
      return 'mobile';
    }
    if (/iPad|Tablet/.test(ua) || (width >= BREAKPOINTS.md && width < BREAKPOINTS.lg)) {
      return 'tablet';
    }
  }

  // General rules for all platforms
  if (width < BREAKPOINTS.md) {
    return 'mobile';
  } else if (width < BREAKPOINTS.lg) {
    return 'tablet';
  } else {
    return 'desktop';
  }
}

/**
 * Get current breakpoint based on width
 */
function getCurrentBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS.xxl) return 'xxl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
}

/**
 * Main responsive hook
 */
export function useResponsive(): ResponsiveInfo {
  const { width, height } = useWindowDimensions();

  // Calculate derived values
  const deviceType = getDeviceType(width);
  const orientation: Orientation = width > height ? 'landscape' : 'portrait';
  const breakpoint = getCurrentBreakpoint(width);

  // Utility functions
  const greaterThan = (bp: Breakpoint) => width >= BREAKPOINTS[bp];
  const lessThan = (bp: Breakpoint) => width < BREAKPOINTS[bp];
  const between = (min: Breakpoint, max: Breakpoint) =>
    width >= BREAKPOINTS[min] && width < BREAKPOINTS[max];

  return {
    // Dimensions
    width,
    height,

    // Device type
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',

    // Orientation
    orientation,
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape',

    // Breakpoints
    breakpoint,
    isXs: breakpoint === 'xs',
    isSm: breakpoint === 'sm',
    isMd: breakpoint === 'md',
    isLg: breakpoint === 'lg',
    isXl: breakpoint === 'xl',
    isXxl: breakpoint === 'xxl',

    // Utility functions
    greaterThan,
    lessThan,
    between,
  };
}

/**
 * Helper hook for getting responsive values
 *
 * @example
 * const spacing = useResponsiveValue({ mobile: 8, tablet: 16, desktop: 24 });
 * const columns = useResponsiveValue({ mobile: 1, tablet: 2, desktop: 3 });
 */
export function useResponsiveValue<T>(values: {
  mobile?: T;
  tablet?: T;
  desktop?: T;
}): T | undefined {
  const { isMobile, isTablet, isDesktop } = useResponsive();

  if (isMobile && values.mobile !== undefined) return values.mobile;
  if (isTablet && values.tablet !== undefined) return values.tablet;
  if (isDesktop && values.desktop !== undefined) return values.desktop;

  // Fallback logic
  return values.desktop ?? values.tablet ?? values.mobile;
}

/**
 * Helper hook for conditional rendering based on breakpoint
 *
 * @example
 * const showSidebar = useBreakpoint('md'); // true if >= md
 * const isMobileOnly = useBreakpoint('sm', 'max'); // true if < sm
 */
export function useBreakpoint(breakpoint: Breakpoint, direction: 'min' | 'max' = 'min'): boolean {
  const { width } = useWindowDimensions();

  if (direction === 'min') {
    return width >= BREAKPOINTS[breakpoint];
  } else {
    return width < BREAKPOINTS[breakpoint];
  }
}

/**
 * Responsive style helper
 * Returns different style objects based on device type
 *
 * @example
 * const containerStyle = useResponsiveStyle({
 *   mobile: { padding: 8, flexDirection: 'column' },
 *   tablet: { padding: 16, flexDirection: 'row' },
 *   desktop: { padding: 24, flexDirection: 'row' }
 * });
 */
export function useResponsiveStyle<T extends Record<string, any>>(styles: {
  mobile?: T;
  tablet?: T;
  desktop?: T;
}): T {
  const { isMobile, isTablet } = useResponsive();

  if (isMobile && styles.mobile) return styles.mobile;
  if (isTablet && styles.tablet) return styles.tablet;
  if (styles.desktop) return styles.desktop;

  // Fallback
  return (styles.desktop || styles.tablet || styles.mobile) as T;
}

export default useResponsive;
